import qs from 'qs';
import Constant from '@/constants/const';
import { PageNameV2 } from '@/constants/track';
import { getPageEventName, getPageEventNameV2 } from '@/tracks/tracker';

import { getPathNoLangOnClient, isServer } from '@/utils/url';
import { getCookie } from '@/utils/cookie';

/** 结账流程中由 useModalRoute 写入 modal_name= 的 PV，与 query 值一致（即 page_key） */
const CHECKOUT_MODAL_PV_PAGE_KEYS = new Set<string>([
  PageNameV2.mweb_payment_method,
  PageNameV2.mweb_ebt_card_add,
  PageNameV2.mweb_payment_new_card
]);

function isCheckoutRelatedPath(pathnameNoLang: string) {
  return (
    pathnameNoLang.startsWith('/order/checkout') ||
    pathnameNoLang.includes('/order/dealpay') ||
    pathnameNoLang.startsWith('/order/nocart-checkout')
  );
}

export function getMyListPageKey(type) {
  switch (type) {
    case 'bought':
      return PageNameV2.mweb_me_my_bought;
    case 'reorder':
      return PageNameV2.mweb_me_my_reorder;
    case 'watchlist':
      return PageNameV2.mweb_me_my_watch_list;
    case 'recently':
      return PageNameV2.mweb_me_my_recently;
    case 'shareBook':
      return 'mweb_me_my_list';
  }
}

export function getCmsPageKey(url: string, returnArray: boolean = false) {
  const defaultUrl = isServer ? '' : location.href;
  const _url = new URL(url || defaultUrl).pathname;
  const _pathArr = _url.split('/');
  if (returnArray) {
    return _pathArr;
  }
  return `mweb_cms_${_pathArr[_pathArr.length - 2]}`; // cms_brand
}

/**
 * 获取url上动态路由的参数
 * 比如/zh/account/notification/community， prefix=/account/notification，返回的是community
 * @param url
 * @param prefix
 */
const getDynamicParamFromUrl = (url: string, prefix: string) => {
  if (!url) return '';
  url = getPathNoLangOnClient(url);
  return url.replace(prefix, '').split('/')?.[1] || '';
};

const isFollowInfoPage = (url: string) => {
  try {
    return /\/social\/user\/[\w-]+\/follow-info/.test(url);
  } catch (error) {
    return false;
  }
};
const isProfilePage = (url: string) => {
  try {
    return /\/social\/user\/[\w-]+\/profile/.test(url);
  } catch (error) {
    return false;
  }
};

const isVendorPage = (pathname: string) => {
  try {
    return /\/mkpl\/vendor\/[\d]+$/.test(pathname);
  } catch (error) {
    return false;
  }
};

export function getPageKey(url: string, params?: Record<string, string>) {
  // 如果在搜索页面没有关键词和搜索结果, 需要处理成别的page_key
  let _url: URL;
  try {
    _url = new URL(url);
  } catch (e) {
    //兼容从 router.asPath 中获取时不需要完整的 url 解析
    return getPageEventNameV2(url);
  }

  //处理cms组件的page_key /zh/cms/page/brand/feLLOXys
  if (url.includes('/cms/page/')) {
    return getCmsPageKey(url);
  }
  //处理notification的二级页面的page_key /zh/account/notification/community
  if (url.includes('/account/notification/')) {
    return `mweb_me_message_center_${getDynamicParamFromUrl(_url.pathname, '/account/notification')}`;
  }

  if (url.includes('/mkpl/search') && !_url.search?.includes('seller_id=') && !_url.search?.includes('keyword=')) {
    return PageNameV2.mweb_mkpl_global_search_landing;
  }

  if (isVendorPage(_url.pathname)) {
    const queryObject = qs.parse(_url.search.replace('?', ''));
    const bizType = (queryObject?.biz_type ?? 'mkpl') as string;

    switch (bizType) {
      case 'fbw':
        return PageNameV2.mweb_fbw_vendor;
      case 'mkpl':
        return PageNameV2.mweb_mkpl_vendor;
      default:
        return PageNameV2.mweb_mkpl_vendor;
    }
  }

  // 和 vendor 一样：在拥有 _url 后再判一次 /goods/catalog/，用 pathname 取 item，不带 query
  if (_url.pathname.includes('/goods/catalog/')) {
    const pathSegments = _url.pathname.split('/').filter(Boolean);
    const item = pathSegments.pop() || '';
    return `${PageNameV2.mweb_pantry_item}_${item}`;
  }

  if (url.includes('/search') && !_url.search?.includes('keyword=')) {
    return PageNameV2.mweb_search_landing;
  }
  if (url.includes('/order/checkout') && url.includes('show_upsell=1')) {
    return PageNameV2.mweb_before_you_checkout;
  }

  const checkoutPathNoLang = getPathNoLangOnClient(_url.pathname);
  if (isCheckoutRelatedPath(checkoutPathNoLang)) {
    const queryObject = qs.parse(_url.search.replace(/^\?/, ''));
    const pmRaw = queryObject._pm ?? queryObject['_pm'];
    const depth = pmRaw != null && pmRaw !== '' ? parseInt(String(pmRaw), 10) : 0;
    const modalName = queryObject.modal_name as string | undefined;
    if (depth >= 1 && modalName && CHECKOUT_MODAL_PV_PAGE_KEYS.has(modalName)) {
      return modalName;
    }
  }

  if (
    url.includes('/account/my-list') &&
    !url.includes('/account/my-list/book') &&
    !url.includes('/account/my-list/new')
  ) {
    const queryObject = qs.parse(_url.search.substring(1));
    const type = queryObject?.type ?? 'watchlist';
    return getMyListPageKey(type);
  }

  if (url.includes('/account/profile')) {
    const pathname = _url.pathname;
    const userId = pathname.split('/').pop();
    const isMyself = userId === getCookie(Constant.UID);

    switch (params?.click_type || 'posts') {
      case 'posts':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_post;
        }
        return PageNameV2.mweb_comm_profile_post;
      case 'reviews':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_review;
        }
        return PageNameV2.mweb_comm_profile_review;
      case 'liked':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_like;
        }
        return PageNameV2.mweb_comm_profile_like;
      case 'commented':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_comment;
        }
        return PageNameV2.mweb_comm_profile_comment;
    }
  }

  if (url.includes('/account/perks')) {
    const queryObject = qs.parse(_url.search.substring(1));
    const tab = (queryObject?.tab ?? 1) as string;

    switch (parseInt(tab)) {
      case 1:
        return PageNameV2.mweb_me_points;
      case 2:
        return PageNameV2.mweb_me_vip;
    }
  }

  if (isProfilePage(_url.pathname)) {
    const pathname = _url.pathname;
    const userId = /\/user\/([\w-]+)\/profile/.exec(pathname)?.[1];
    const isMyself = userId === getCookie(Constant.UID);
    const queryObject = qs.parse(_url.search.replace('?', ''));
    const tab = (queryObject?.tab ?? 'posts') as string;

    switch (tab) {
      case 'posts':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_post;
        }
        return PageNameV2.mweb_comm_profile_post;
      case 'reviews':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_review;
        }
        return PageNameV2.mweb_comm_profile_review;
      case 'liked':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_like;
        }
        return PageNameV2.mweb_comm_profile_like;
      case 'commented':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_comment;
        }
        return PageNameV2.mweb_comm_profile_comment;
    }
  }

  if (isFollowInfoPage(_url.pathname)) {
    const pathname = _url.pathname;

    const userId = /\/user\/([\w-]+)\/follow-info/.exec(pathname)?.[1];
    const isMyself = userId === getCookie(Constant.UID);
    const queryObject = qs.parse(_url.search.replace('?', ''));
    const tab = (queryObject?.tab ?? 'follower') as string;

    switch (tab) {
      case 'follower':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_follower;
        } else {
          return PageNameV2.mweb_comm_profile_follower;
        }
      case 'following':
        if (isMyself) {
          return PageNameV2.mweb_me_profile_following;
        } else {
          return PageNameV2.mweb_comm_profile_following;
        }
      default:
        if (isMyself) {
          return PageNameV2.mweb_me_profile_follower;
        } else {
          return PageNameV2.mweb_comm_profile_follower;
        }
    }
  }
  return getPageEventNameV2(getPathNoLangOnClient(_url.pathname));
}

export function getOldPageKey() {
  return getPageEventName(getPathNoLangOnClient());
}
